import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { _columns } from 'common/components/ActionMenu/Attach/_columns.data';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { useStyles } from 'common/components/ActionMenu/Attach/AttachFormModal/index.styles';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { cutToast } from 'utility/toast';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import { Trip_trip } from '../../../../../__generated__/Trip';
import { TripAvailableDocumentTypes } from '../../__generated__/TripAvailableDocumentTypes';
import { toOrderByVariable } from '../utils';
import {
  AttachableTripDocuments,
  AttachableTripDocuments_attachableTripDocuments_nodes,
} from './__generated__/AttachableTripDocuments';
import { AttachTripDocumentVariables } from './__generated__/AttachTripDocument';

import ATTACHABLE_TRIP_DOCUMENTS from './AttachableTripDocuments.graphql';
import ATTACH_TRIP_DOCUMENT from './AttachTripDocument.graphql';
import TRIP from '../../../../../Trip.graphql';

type AttachableDataType = AttachableTripDocuments_attachableTripDocuments_nodes;

interface AttachFormModalProps {
  availableDocumentTypes: TripAvailableDocumentTypes | undefined;
  tripData: Trip_trip | null | undefined;
  isDirty: boolean;
}
export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  availableDocumentTypes,
  tripData,
  isDirty,
}) => {
  const styles = useStyles();
  const { id } = { ...tripData };
  const getDocumentTypeOptions = useCallback(
    (data: TripAvailableDocumentTypes | undefined) => {
      return (
        data?.tripAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title: doctype.documentType || undefined,
          disabled: undefined,
        })) || []
      );
    },
    []
  );
  const transformData = useCallback(
    (data: AttachableTripDocuments | undefined) => {
      return data?.attachableTripDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (
      selectedItems: AttachableDataType[],
      entityId: string,
      documentTypeId?: number | null
    ) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          tripId: entityId,
          entityDocumentId: entityDocIds,
          documentTypeId: documentTypeId ?? null,
        },
      } as AttachTripDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;
      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{item.documentTypes?.documentType}</Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{item.currency?.isoCode}</Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    },
    [styles]
  );

  return (
    <GenericAttachForm
      entity={{ id: id! }}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_TRIP_DOCUMENTS}
      attachDocumentsMutation={ATTACH_TRIP_DOCUMENT}
      detailsQuery={TRIP}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        tripId: id!,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
